/**
 * Pure logic for the guest activity room (`/apps/session/join/:token`).
 *
 * Kept separate from GuestActivityRoom.tsx so the payload parsing and state
 * derivation can be unit-tested without mounting React or a Supabase client.
 *
 * Everything here operates on the `get_guest_activity` payload (migration 740).
 * A guest reads no table directly: `tool_sessions_select` (migration 483) is
 * keyed on `auth.uid()`, so an unauthenticated caller saw nothing at all before
 * that RPC existed.
 */

import type { SessionReadout, ToolItem } from '../../contexts/ToolItemsContext';

/** One element of the sanitized `activity.items` array. */
export interface GuestToolSessionItem {
  id: string;
  title: string;
  description?: string | null;
  sourceId?: string | null;
}

/** The tool activity a guest was invited to answer (non-group sessions). */
export interface GuestToolActivity {
  id: string;
  toolType: string | null;
  toolName: string | null;
  phase: string | null;
  items: GuestToolSessionItem[];
  /** The session's tool settings (dots, budget share, Pareto band) — migration 964. */
  toolConfig: Record<string, unknown>;
  /** What a member's screen reads from the session row — migration 964. */
  readout: SessionReadout;
}

/** One thing a guest is judging in a group session. */
export interface GuestOption {
  id: string;
  title: string;
  description: string;
  url: string | null;
}

/** One weighted dimension a group session scores its options on. */
export interface GuestCriterion {
  id: string;
  title: string;
  description: string;
  weight: number;
  position: number;
}

/** A score this guest already recorded, keyed by option + criterion. */
export interface GuestPriorScore {
  itemId: string;
  criteriaId: string;
  score: number;
  note: string | null;
}

export type GuestActivityStatus = 'open' | 'closed' | 'not_started';

/** The parsed, client-shaped `get_guest_activity` result. */
export interface GuestActivity {
  status: GuestActivityStatus;
  mode: 'group' | 'tool';
  sessionId: string;
  sessionName: string;
  activity: GuestToolActivity | null;
  options: GuestOption[];
  criteria: GuestCriterion[];
  /** Tool mode: whatever the guest submitted last time, if anything. */
  toolResponse: unknown;
  /** Group mode: this guest's own prior scores. */
  priorScores: GuestPriorScore[];
}

export type GuestActivityState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  /** The host hasn't opened an activity yet — this screen polls for it. */
  | { kind: 'waiting' }
  /** Scoring/collection is over. Nothing left for a guest to do. */
  | { kind: 'closed' }
  | {
      kind: 'ready';
      toolSessionId: string;
      toolType: string | null;
      items: ToolItem[];
      toolConfig: Record<string, unknown>;
      readout: SessionReadout;
    }
  | {
      kind: 'scoring';
      options: GuestOption[];
      criteria: GuestCriterion[];
      priorScores: GuestPriorScore[];
    }
  | { kind: 'submitted' };

/** `activity.items` (title/sourceId shape) → ToolItem (name/sourceId). */
export function mapGuestToolSessionItems(items: GuestToolSessionItem[]): ToolItem[] {
  return items.map((item) => ({
    id: item.id,
    name: item.title || 'Item',
    description: item.description ?? '',
    sourceId: item.sourceId ?? undefined,
  }));
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.map(asRecord) : [];
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** The session context an open activity carries for a guest (migration 964). */
export function parseSessionReadout(activityRow: Record<string, unknown>): SessionReadout {
  const criteriaConfig = asRecord(activityRow.criteria_config);
  const weights = asRecord(criteriaConfig.weights);
  const effortAttribute = activityRow.effort_attribute ? asRecord(activityRow.effort_attribute) : null;
  return {
    criteriaConfig: {
      criteriaIds: (Array.isArray(criteriaConfig.criteriaIds) ? criteriaConfig.criteriaIds : []).filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
      ),
      weights: Object.fromEntries(
        Object.entries(weights).flatMap(([k, v]) => (num(v) === undefined ? [] : [[k, num(v)!]])),
      ),
    },
    engineCriteria: asArray(activityRow.engine_criteria)
      .filter((c) => str(c.id))
      .map((c) => ({ id: str(c.id), name: str(c.name), description: str(c.description), weight: num(c.weight) })),
    criterionLabels: Object.fromEntries(
      Object.entries(asRecord(activityRow.criterion_labels)).map(([key, v]) => {
        const label = asRecord(v);
        return [key, { label: str(label.label), description: str(label.description) }];
      }),
    ),
    itemEfforts: Object.fromEntries(
      Object.entries(asRecord(activityRow.item_efforts)).map(([itemId, v]) => {
        const e = asRecord(v);
        return [itemId, { value: e.value, scaleId: typeof e.scale_id === 'string' ? e.scale_id : null }];
      }),
    ),
    effortAttribute: effortAttribute
      ? {
          scales: asArray(effortAttribute.scales).map((sc) => ({
            id: str(sc.id),
            isDefault: sc.isDefault === true,
            points: asArray(sc.points).flatMap((pt) =>
              num(pt.value) === undefined ? [] : [{ value: num(pt.value)!, label: str(pt.label) }],
            ),
          })),
          defaultScaleId: typeof effortAttribute.default_scale_id === 'string' ? effortAttribute.default_scale_id : null,
          options: (Array.isArray(effortAttribute.options) ? effortAttribute.options : []).filter(
            (o): o is string => typeof o === 'string',
          ),
        }
      : null,
  };
}

/**
 * Turn the `get_guest_activity` jsonb into the shape this screen renders.
 *
 * `{ success: false, error }` is the RPC's answer for a dead, revoked, expired
 * or unknown link — the message is already a plain sentence, so it is passed
 * through untouched.
 */
export function parseGuestActivity(
  raw: unknown,
): { ok: true; data: GuestActivity } | { ok: false; error: string } {
  const row = asRecord(raw);
  if (row.success === false) {
    return { ok: false, error: str(row.error, 'This link is not valid') };
  }
  if (row.success !== true) {
    return { ok: false, error: 'Could not load this session.' };
  }

  const rawStatus = str(row.status);
  const status: GuestActivityStatus =
    rawStatus === 'open' || rawStatus === 'closed' || rawStatus === 'not_started'
      ? rawStatus
      : 'closed';

  const session = asRecord(row.session);
  const activityRow = row.activity ? asRecord(row.activity) : null;

  return {
    ok: true,
    data: {
      status,
      mode: str(row.mode) === 'group' ? 'group' : 'tool',
      sessionId: str(session.id),
      sessionName: str(session.name),
      activity:
        activityRow && str(activityRow.tool_session_id)
          ? {
              id: str(activityRow.tool_session_id),
              toolType: typeof activityRow.tool_type === 'string' ? activityRow.tool_type : null,
              toolName: typeof activityRow.tool_name === 'string' ? activityRow.tool_name : null,
              phase: typeof activityRow.phase === 'string' ? activityRow.phase : null,
              items: asArray(activityRow.items).map((item) => ({
                id: str(item.id),
                title: str(item.title),
                description: str(item.description),
                sourceId: typeof item.sourceId === 'string' ? item.sourceId : null,
              })),
              toolConfig: asRecord(activityRow.tool_config),
              readout: parseSessionReadout(activityRow),
            }
          : null,
      options: asArray(row.options).map((opt) => ({
        id: str(opt.id),
        title: str(opt.title, 'Option'),
        description: str(opt.description),
        url: typeof opt.url === 'string' ? opt.url : null,
      })),
      criteria: asArray(row.criteria).map((c, index) => ({
        id: str(c.id),
        title: str(c.title, 'Criterion'),
        description: str(c.description),
        weight: typeof c.weight === 'number' ? c.weight : Number(c.weight ?? 1) || 1,
        position: typeof c.position === 'number' ? c.position : index,
      })),
      toolResponse: row.my_response ?? null,
      priorScores:
        str(row.mode) === 'group'
          ? asArray(row.my_response).map((s) => ({
              itemId: str(s.item_id),
              criteriaId: str(s.criteria_id),
              score: Number(s.score ?? 0),
              note: typeof s.note === 'string' ? s.note : null,
            }))
          : [],
    },
  };
}

/**
 * Single source of truth for what the guest activity screen shows. Mirrors
 * ParticipantAsyncToolRoom's phases (loading / waiting / tool / submitted)
 * without any of the authenticated-participant data dependencies, plus the two
 * states only a guest link has: a session that has closed under them, and the
 * group-scoring surface.
 */
export function deriveGuestActivityState(input: {
  loading: boolean;
  loadError: string | null;
  submitted: boolean;
  activity: GuestActivity | null;
}): GuestActivityState {
  if (input.submitted) return { kind: 'submitted' };
  if (input.loadError) return { kind: 'error', message: input.loadError };
  if (input.loading) return { kind: 'loading' };
  if (!input.activity) return { kind: 'waiting' };

  const data = input.activity;
  if (data.status === 'closed') return { kind: 'closed' };
  if (data.status === 'not_started') return { kind: 'waiting' };

  if (data.mode === 'group') {
    if (data.criteria.length === 0 || data.options.length === 0) return { kind: 'waiting' };
    return {
      kind: 'scoring',
      options: data.options,
      criteria: data.criteria,
      priorScores: data.priorScores,
    };
  }

  if (!data.activity) return { kind: 'waiting' };
  return {
    kind: 'ready',
    toolSessionId: data.activity.id,
    toolType: data.activity.toolType,
    items: mapGuestToolSessionItems(data.activity.items),
    toolConfig: data.activity.toolConfig,
    readout: data.activity.readout,
  };
}
